/**
 * plan_write_state — Surgically update the YAML state block in a Plan entity.
 *
 * Merges stateUpdates into the existing YAML state block without replacing
 * the rest of the document. Also updates skeleton.status if workflow.status
 * is included in the updates.
 *
 * Parameters:
 *   entityId: Plan entity ID
 *   stateUpdates: object to deep-merge into the existing state
 *
 * Example:
 *   plan_write_state({
 *     entityId: "plan_cos_setup",
 *     stateUpdates: {
 *       "workflow.current_step": "integrations",
 *       "workflow.status": "in_progress",
 *       "integrations.gmail.connected": true
 *     }
 *   })
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { getApplicationForEntity } from '../../services/applicationRegistry';

function serializeToYaml(obj: Record<string, any>, indent = 0): string {
  const pad = ' '.repeat(indent);
  const lines: string[] = [];
  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === undefined) {
      lines.push(`${pad}${key}: null`);
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      lines.push(`${pad}${key}:`);
      lines.push(serializeToYaml(value, indent + 2));
    } else if (Array.isArray(value)) {
      if (value.length === 0) {
        lines.push(`${pad}${key}: []`);
      } else {
        lines.push(`${pad}${key}:`);
        for (const item of value) {
          lines.push(`${pad}  - ${JSON.stringify(item)}`);
        }
      }
    } else {
      lines.push(`${pad}${key}: ${value}`);
    }
  }
  return lines.join('\n');
}

function setNestedPath(obj: Record<string, any>, dotPath: string, value: any): void {
  const keys = dotPath.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
      current[keys[i]] = {};
    }
    current = current[keys[i]];
  }
  current[keys[keys.length - 1]] = value;
}

export const plan_write_state: SimpleDocCommand = {
  id: 'plan_write_state',
  logicalName: 'plan_write_state',
  version: 'v1',

  description: `Update the YAML state block in a Plan entity.

Merges stateUpdates into the existing state without touching the rest of the document.
Use dot-notation keys to update nested fields.

If workflow.status is updated to "complete", also marks the Plan entity skeleton.status = "complete".

**EXAMPLE:**
{
  "entityId": "plan_cos_setup",
  "stateUpdates": {
    "workflow.current_step": "bootstrap",
    "workflow.status": "in_progress",
    "integrations.gmail.connected": true
  }
}`,

  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'Plan entity ID (plan_*)',
        required: true,
      },
      {
        name: 'stateUpdates',
        type: 'object',
        description: 'Key-value pairs to merge into the YAML state. Use dot-notation for nested keys.',
        required: true,
      },
    ],
  },

  examples: {
    good: [{
      input: { entityId: 'plan_cos_setup', stateUpdates: { 'workflow.current_step': 'integrations' } },
      expectedOutput: createSuccessResult('State updated', {}),
      description: 'Advance to integrations step',
    }],
    bad: [],
  },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId, stateUpdates } = params;
    if (!entityId) return createErrorResult('Missing entityId', 'entityId is required', 'validation');
    if (!stateUpdates || typeof stateUpdates !== 'object') {
      return createErrorResult('Missing stateUpdates', 'stateUpdates must be an object', 'validation');
    }

    try {
      const app = await getApplicationForEntity(entityId);
      if (!app) {
        return createErrorResult('Plan not found', `No application for ${entityId}`, 'validation');
      }

      // Read current YAML state
      const readResult = await app.handleEntityRead({ className: 'page-code' }, {});
      const nodes: any[] = readResult.nodes ?? [];
      const yamlNode = nodes.find((n: any) => n.className === 'page-code' && n.attrs?.language === 'yaml');

      // Parse existing state
      let currentState: Record<string, any> = {};
      if (yamlNode?.attrs?.text) {
        try {
          // Import the parser from plan_read via dynamic import
          const { plan_read } = await import('./plan_read');
          const planReadResult = await plan_read.handler!({ entityId }, {} as any);
          currentState = (planReadResult as any)?.data?.state ?? {};
        } catch {
          // fallback: keep empty state
        }
      }

      // Apply dot-notation updates
      for (const [dotKey, value] of Object.entries(stateUpdates)) {
        setNestedPath(currentState, dotKey, value);
      }

      const newYaml = serializeToYaml(currentState);

      // Write the updated YAML back to the page-code block
      if (yamlNode?.id) {
        await app.handleEntityWrite({
          operation: 'update',
          target: { nodeId: yamlNode.id },
          attrs: { text: newYaml, language: 'yaml' },
        });
      } else {
        // No existing YAML block — insert one
        await app.handleEntityWrite({
          operation: 'insert',
          target: { at: 'end' },
          content: { className: 'page-code', attrs: { text: newYaml, language: 'yaml' } },
        });
      }

      // If workflow.status is being updated, also update the entity skeleton.status
      const newWorkflowStatus =
        (stateUpdates as any)['workflow.status'] ||
        (stateUpdates as any)?.workflow?.status;

      if (newWorkflowStatus) {
        try {
          const entityStore = (await import('../../stores/entityStore')).default.getState();
          const entity = entityStore.getEntityById(entityId) || entityStore.getEntityByLlmId(entityId);
          if (entity?._id) {
            await entityStore.actions.updateEntity(entity._id, {
              skeleton: { ...entity.skeleton, status: newWorkflowStatus },
            });
          }
        } catch (err) {
          console.warn('[plan_write_state] Failed to update skeleton.status (non-fatal):', err);
        }
      }

      return createSuccessResult('Plan state updated', {
        entityId,
        updatedKeys: Object.keys(stateUpdates),
        newWorkflowStatus: newWorkflowStatus || null,
      });
    } catch (error: any) {
      return createErrorResult('Failed to update plan state', error.message || 'Unknown error', 'execution');
    }
  },
};
